import clsx from 'clsx';
import Style from './loadResource.module.scss';
import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { TypographyStyles } from 'src/styles';
import { useUploadPicture as useUploadResource } from './useUploadResourceModal';
import { AppButton } from 'uikit/buttons';
import { AppInputError } from 'uikit/wrappers';
import { Loading } from 'uikit/suspense/Loading';
import { ConfirmResult } from 'src/components/dialogModal/useConfirmationModal';
import { LayoutAttachmentType } from 'src/features/generalForms/context/attachments.context';

type LoadResourceBlockProps = {
  addButtonText: string;
  changeButtonText: string;
  placeholderImg: string;
  error?: string;
  isLoading: boolean;
  value: LayoutAttachmentType | null;
  onChange: (appAttachment: LayoutAttachmentType | null) => void;
};

export const LoadResourceBlock: React.FC<PropsWithChildren<LoadResourceBlockProps>> = (
  props: PropsWithChildren<LoadResourceBlockProps>,
) => {
  const { addButtonText, changeButtonText, placeholderImg, error, isLoading, onChange, value } = props;

  const isOverlay = value && !error;
  const pictureModal = useUploadResource();

  const onChangeHandler = useCallback(async () => {
    const result = await pictureModal.open(value ? [value] : []);
    if (result.result === ConfirmResult.Confirm) {
      onChange(result.data);
    }
  }, [pictureModal, value, onChange]);

  const overlayComponent = useMemo(() => {
    return (
      <div className={Style.imageOverlayContainer}>
        {props.children}
        {!isLoading && (
          <div className={Style.imageOverlay}>
            <AppButton colorSchema={'primary'} variant={'button'} text={changeButtonText} onClick={onChangeHandler} />
          </div>
        )}
      </div>
    );
  }, [props.children, isLoading, changeButtonText, onChangeHandler]);

  const selectorComponent = useMemo(() => {
    return (
      <div className={clsx(TypographyStyles.heading2, Style.imageTextContainer)}>
        <div>
          <img src={placeholderImg} alt={'Resource icon'} className={Style.resourceIcon} />
          <AppButton variant={'icon-link'} colorSchema={'primary'} text={addButtonText} onClick={onChangeHandler} />
        </div>
        <AppInputError errors={error} hideBorder={true} className={Style.errorOverride} />
      </div>
    );
  }, [placeholderImg, addButtonText, onChangeHandler, error]);

  return (
    <Loading loading={isLoading && !error} containerStyle={{ height: '100%' }} testId={'load-resource-component'}>
      {isOverlay ? overlayComponent : selectorComponent}
      {pictureModal.modal}
    </Loading>
  );
};
